import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronUpIcon, ChevronDownIcon, ScanLineIcon, Loader2Icon,
  Building2Icon, EyeIcon, AlertTriangleIcon, Trash2Icon, ActivityIcon, UserPlusIcon, InboxIcon, CalendarClockIcon,
  GlobeIcon, CarFrontIcon, StoreIcon, type LucideIcon,
} from 'lucide-react';

import { cn } from '../lib/cn';
import { shortDateOrNull } from '../lib/oura';
import { NewScanModal } from '../components/NewScanModal';
import { DeleteBusinessModal } from '../components/DeleteBusinessModal';
import { useScanJobs, type StartResult } from '../aeo';
import { listBusinessesWithCounts, deleteBusiness, type AeoBusiness, type AeoBuyMode, type AeoStageCounts } from '@service/aeo';
import { useSnackbar } from '@hook/use-snackbar';
import { listStageOf, LIST_STAGE_DOT, LIST_STAGE_LABEL, LIST_STAGE_ORDER, type ListStage } from '../lifecycle';
import { myToday } from '../components/docs/boardModel';
import { BUY_LABEL, whereOf } from '../buyMode';
import { getOnboardingPlanFacts, listOnboardings, listUnmatchedAnswers, type Onboarding, type UnmatchedAnswer } from '@service/aeo/onboarding';
import { FIRST_CHECK_MINUTES, onboardingSummary, type OnboardingPlanFacts, type PlanRead } from '../onboarding/derive';
import { FORM_ANSWERS_ON } from '@service/aeo/formResponses';
import { NewClientDialog } from '../components/onboarding/NewClientDialog';
import { OURA_ROOT, ouraHref, showOnboarding, type View } from '../nav';
import { SCAN_DRAFT_UI_ON, scanDraftMode } from '../newscan/flags';
import { useScanningSites } from '../newscan/useScanningSites';
import { siteKey } from '../newscan/domain';
import { UnmatchedAnswersNotice } from '../components/onboarding/UnmatchedAnswers';
import {
  FOCUS_RING, FoldScope, GroupFold, LATE_TEXT, LogoSquare, faviconOf, OfValue, PageHead, PillButton, PillLink, SearchBox, Seg, SURFACE, Surface, Tag, Tile, Tiles, TYPE,
  type Tone,
} from '@oura/ui';
import { OpenDraftRows } from '@oura/components/newscan/OpenDraftList';
import { listOpenScanDrafts } from '@service/aeo/scanDraft';
import { draftPurpose, type ScanDraftListItem } from '@service/aeo/scanDraftTypes';
import { canTrash, factsFromRow, hubDraftOf, hubStage, rowAction, type HubStage } from '../overview/hubStage';
import { canSignClients } from '../overview/useHubStage';
import { useDraftNoticeSnackbar, useHubActions } from '../overview/useHubActions';
import { useVisiblePoll } from '../lib/useVisiblePoll';
import { RescanSheet } from '../components/RescanSheet';
import { EMPTY_CELL } from '../components/client/staffScreen';
import { ASK_OURA_ON, AskOuraBar } from '@oura/ask';
import { AllClientDocs } from './AllClientDocs';
import { TodayList } from './TodayList';
import { DELIVERY_ON } from '../delivery/flag';
import { useBoard } from '../board/useBoard';
import { cardOf, type ClientCard } from '../board/model';
import { focusesOf, rankClients, RENEWAL_DAYS } from '../businesses/focus';
import { DeliveryHealth, NextUp, ScoreBar, ScoreSpread, StartHere, TermBar, type DeliveryRead } from '../businesses/FocusPanels';
import { Bento, BentoStack } from '@oura/ui';

/**
 * A new client made on sample data exists only in the sample store. Where the
 * list itself is live it would never show, so New client waits for its own
 * endpoints there. Start onboarding on a business already in the list still works.
 */
const canCreateClient = canSignClients;

type SortKey = 'name' | 'aeo' | 'seo' | 'lastScan' | 'added' | 'quiet' | 'ends' | 'focus';
/** The page's views: the pipeline table, every client's next steps (?view=today) and All clients' documents (?view=documents). */
type BusinessesView = 'businesses' | 'documents' | 'today';
/** The one tab panel under the view tabs; each tab's id is `${VIEW_PANEL_ID}-tab-<key>`. */
const VIEW_PANEL_ID = 'businesses-view-panel';
/** Plan-facts reads (New scan switch on only): at once, per pass, and how long a good read is kept. */
const PLAN_READ_PARALLEL = 4;
const PLAN_READ_CAP = 20;
const PLAN_FACTS_TTL_MS = 60_000;
/** A failed plan read is asked again this many times, this far apart. */
const PLAN_RETRY_MAX = 3;
const PLAN_RETRY_MS = 15_000;
// 'pending' = no terminal assessment yet (ourteam-backend#3747's
// `latest_assessment: null`) — distinct from 'gated' (ran, but produced no
// usable score) and 'scored' (has real aeo_score/seo_score, including 0).
type RowStatus = 'gated' | 'scored' | 'pending';

/**
 * The house table, one line per cell: sentence-case 13px heads, hairlines
 * between rows, 52px rows. A second line under a value used to hold a second
 * fact; each such fact now has its own column (YW, 15 Oct 2026: "if we need
 * subtext, consider splitting to its own column").
 */
const TH = 'h-11 w-px whitespace-nowrap px-2.5 text-[13px] font-medium leading-[18px] text-gray-500 first:pl-5';
const TD = 'h-[52px] whitespace-nowrap border-t border-gray-100 px-2.5 align-middle text-sm first:pl-5';

/**
 * The filter pill: a light ourteam-blue tint when on, white with a hairline
 * when off. Not the kit's ink fill: YW read a near-black pill as a colour from
 * nowhere (15 Oct 2026), and blue is the one accent the page already uses.
 */
function pillClass(on: boolean): string {
  return cn(
    TYPE.tc,
    // 36px on a phone, where it is tapped; 32px beside a pointer.
    'inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3.5 font-medium ring-1 ring-inset transition-colors sm:h-8',
    on ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-white text-gray-900 ring-gray-200 hover:bg-gray-50',
    FOCUS_RING,
  );
}

/** A count inside a pill: quieter than the label, blue on the pill that is on. */
const pillCount = (on: boolean) => cn('tabular-nums', on ? 'text-blue-500' : 'text-gray-400');

/**
 * THE STAGE TABS over the table (YW, 15 Oct 2026): paying clients and the long
 * tail of prospects are two different jobs, and one table mixed them. Each tab
 * is one `ListStage` plus All, kept in the URL as ?stage= so a tab deep-links.
 */
type StageTab = ListStage | 'all';
const STAGE_TABS: readonly StageTab[] = [...LIST_STAGE_ORDER, 'all'];
const STAGE_TAB_LABEL: Record<StageTab, string> = {
  active: 'Active plans',
  onboarding: 'Onboarding',
  prospect: 'Prospects',
  ended: 'Ended',
  all: 'All',
};

function readStageTab(raw: string | null): StageTab | null {
  return raw && (STAGE_TABS as readonly string[]).includes(raw) ? (raw as StageTab) : null;
}

type ColKey =
  | 'next' | 'plan' | 'term' | 'ends' | 'endedOn' | 'timeline' | 'setUp' | 'nextStep'
  | 'aeo' | 'seo' | 'stage' | 'category' | 'where' | 'sells' | 'lastScan' | 'quiet' | 'requestedBy' | 'added' | 'website';

/**
 * Each tab shows the columns its job needs, in three tiers so no cell is ever
 * cut off and no tab scrolls sideways on a 1280px laptop (content about 900px
 * wide): the core set always, `wide` from 1440px, `xwide` from 1680px, where
 * the page reaches its 1280px cap. A client is read by its plan and when it
 * ends, a client being set up by what is left to do (and its scores once the
 * first scan is in, a dash before), a prospect by its scores.
 */
const COLUMNS: Record<StageTab, readonly ColKey[]> = {
  active: ['next', 'term', 'ends', 'aeo', 'seo', 'quiet', 'plan', 'timeline', 'website'],
  onboarding: ['setUp', 'nextStep', 'aeo', 'seo', 'plan', 'term', 'timeline', 'quiet', 'added', 'website'],
  prospect: ['aeo', 'seo', 'where', 'sells', 'category', 'lastScan', 'quiet', 'website'],
  ended: ['plan', 'term', 'endedOn', 'aeo', 'lastScan', 'website'],
  all: ['stage', 'aeo', 'seo', 'where', 'sells', 'category', 'lastScan', 'quiet'],
};

type Tier = 'wide' | 'xwide';
const TIER: Record<StageTab, Partial<Record<ColKey, Tier>>> = {
  active: { plan: 'wide', timeline: 'xwide', website: 'xwide' },
  onboarding: { seo: 'wide', term: 'wide', timeline: 'xwide', website: 'xwide' },
  prospect: { sells: 'wide', category: 'wide', website: 'xwide' },
  ended: { website: 'xwide' },
  all: { sells: 'wide', category: 'xwide' },
};
const TIER_CLASS: Record<Tier, string> = {
  wide: 'hidden min-[1440px]:table-cell',
  xwide: 'hidden min-[1680px]:table-cell',
};
const tierOf = (tab: StageTab, c: ColKey) => {
  const t = TIER[tab][c];
  return t ? TIER_CLASS[t] : undefined;
};

/** Where each tab opens: the plans ending soonest, the newest client being set up, the latest scan. */
const DEFAULT_SORT: Record<StageTab, { key: SortKey; dir: 'asc' | 'desc' }> = {
  active: { key: 'focus', dir: 'asc' },
  onboarding: { key: 'added', dir: 'desc' },
  prospect: { key: 'lastScan', dir: 'desc' },
  ended: { key: 'ends', dir: 'desc' },
  all: { key: 'lastScan', dir: 'desc' },
};

const HEAD: Record<ColKey, { label: string; sort?: SortKey; align: 'left' | 'center'; title?: string }> = {
  next: { label: 'Next up', sort: 'focus', align: 'left', title: 'The one thing to do first for this client, worst first: waiting on us, no dates, late work, gone quiet, low score, renewal.' },
  plan: { label: 'Plan', align: 'left' },
  term: { label: 'Term', align: 'left' },
  ends: { label: 'Ends', sort: 'ends', align: 'left', title: "The term's last day. Amber from 60 days out: the renewal conversation." },
  endedOn: { label: 'Ended', sort: 'ends', align: 'left' },
  timeline: { label: 'Timeline', align: 'left', title: 'Whether the client has a published timeline of dates.' },
  setUp: { label: 'Set-up', align: 'left', title: 'Set-up steps done, of all of them.' },
  nextStep: { label: 'Next step', align: 'left' },
  aeo: { label: 'AEO score', sort: 'aeo', align: 'center' },
  seo: { label: 'Site health', sort: 'seo', align: 'center' },
  // Stage is where the business is commercially; a gated scan shows in the
  // score columns. Two different questions in two places: collapsing them is
  // how "gated" ended up reading as a commercial state.
  stage: { label: 'Stage', align: 'left' },
  category: { label: 'Category', align: 'left' },
  // Was City, which read an online store's country as its town.
  where: { label: 'Where', align: 'left', title: "Where customers find them: the outlets' city, or the country an online store sells to." },
  sells: { label: 'Sells', align: 'left', title: 'How customers buy: at the outlets, online, both, or the business goes to them.' },
  lastScan: { label: 'Last scan', sort: 'lastScan', align: 'center' },
  quiet: {
    label: 'Quiet',
    sort: 'quiet',
    align: 'center',
    title: 'Longest since anything happened — the later of the last scan and the last thing we delivered. A business nothing has ever been done for counts as the quietest.',
  },
  // Who asked for the last scan, not who owns the account.
  requestedBy: { label: 'Requested by', align: 'left' },
  added: { label: 'Added', sort: 'added', align: 'center' },
  website: { label: 'Website', align: 'left' },
};

/** What an empty tab says. Only the prospect tabs offer a scan: a scan does not make a client. */
const EMPTY_TAB: Record<StageTab, { title: string; description: string; scan: boolean }> = {
  active: { title: 'No active plans', description: 'A client shows here once its plan has started and its set-up is finished.', scan: false },
  onboarding: { title: 'Nobody is being set up', description: 'A signed client shows here until its set-up is finished and its plan has started.', scan: false },
  prospect: { title: 'No prospects yet', description: 'Run a New Scan to start building the pipeline.', scan: true },
  ended: { title: 'No ended plans', description: 'A client shows here once its term is over or its plan is cancelled.', scan: false },
  all: { title: 'No businesses scanned yet', description: 'Run a New Scan to start building the pipeline.', scan: true },
};

/** A plan this close to its last day is a renewal to raise now. */
const RENEWAL_WINDOW_DAYS = 60;

/** Whole days from `today` to a DATE-ONLY `day`; negative once it has passed. */
function daysUntil(day: string, today: string): number {
  return Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}

/**
 * The row's icon square shows the business's own favicon. While there is none
 * (or it fails to load) it says how the business sells instead. The usual case
 * is grey furniture; online and home visits are tinted so they stand out.
 */
const BUY_LEAD: Record<AeoBuyMode, { icon: LucideIcon; tone: Tone }> = {
  visit: { icon: Building2Icon, tone: 'gray' },
  online: { icon: GlobeIcon, tone: 'indigo' },
  both: { icon: StoreIcon, tone: 'indigo' },
  we_go_to_them: { icon: CarFrontIcon, tone: 'teal' },
};

/**
 * The stage filter: a filter pill with a dot and a count, every stage shown in
 * order and all one width (YW, 15 Oct 2026). Not the kit's StatusKey: that one
 * leads with All, hides an empty stage and turns a second press into All, and
 * here a stage with no one in it is still an answer.
 */
function StagePills({ value, counts, onChange }: {
  value: StageTab;
  /** Null while the list loads: no count rather than a 0. */
  counts: Record<StageTab, number> | null;
  onChange: (next: StageTab) => void;
}) {
  return (
    // -m-1 p-1: room for the focus ring inside the sideways scroll.
    <div role="group" aria-label="Show businesses by stage" className="-m-1 inline-grid max-w-[calc(100%+0.5rem)] auto-cols-[1fr] grid-flow-col gap-2 overflow-x-auto p-1 scrollbar-hide">
      {STAGE_TABS.map((key) => {
        const on = key === value;
        return (
          <button key={key} type="button" aria-pressed={on} onClick={() => onChange(key)} className={pillClass(on)}>
            {key !== 'all' && <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', LIST_STAGE_DOT[key])} />}
            {STAGE_TAB_LABEL[key]}
            {counts && <span className={pillCount(on)}>{counts[key]}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** "In 45 days", "Tomorrow", "Today": how near the last day is, as a staffer would say it. */
function untilLabel(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

/**
 * How long a business has been quiet, as a sortable number.
 *
 * Never scanned ranks quietest of all — `-Infinity`, so ascending puts it
 * first. Sorting the raw date string instead buries exactly the businesses the
 * sort exists to surface: an empty string sorts below every real timestamp.
 */
function quietRank(lastScan: string | null, lastDelivery?: string | null): number {
  // The LATER of the two, because "quiet" means nothing has happened — and a
  // business scanned in March but delivered to last week is not quiet. Until
  // ourteam-backend#4908 this could only see the scan, and the tooltip said so
  // rather than implying it knew about deliveries.
  const times = [lastScan, lastDelivery]
    .map((d) => (d ? new Date(d).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  // Nothing at all: never scanned AND never delivered to. Quietest there is, so
  // it sorts ahead of every real date rather than to the bottom — which is what
  // `(a ?? '')` would do, and why this is not just `lastScan` reversed.
  return times.length === 0 ? -Infinity : Math.max(...times);
}

/** The moment the Quiet column measures from, or null when there is none. */
function quietSince(lastScan: string | null, lastDelivery?: string | null): number | null {
  const t = quietRank(lastScan, lastDelivery);
  return Number.isFinite(t) ? t : null;
}

/** Whole days since anything happened, as the operator would say it. */
function quietLabel(since: number): string {
  const days = Math.floor((Date.now() - since) / 86_400_000);
  if (Number.isNaN(days)) return '—';
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day';
  if (days < 61) return `${days} days`;
  return `${Math.floor(days / 30)} months`;
}

/**
 * Quiet tone. Deliberately only two steps and no green: a recent scan is not an
 * achievement to colour, it is the normal state. Amber and red are for the rows
 * worth a phone call.
 */
function quietTone(since: number): string {
  const days = Math.floor((Date.now() - since) / 86_400_000);
  if (days >= 60) return 'text-rose-600';
  if (days >= 30) return 'text-amber-700';
  return 'text-gray-500';
}

/**
 * "2026-07-27T15:37:13Z" -> "27 Jul 2026", the business header's own format
 * (the en-GB locale would print "Sept"). An unreadable value is an empty cell.
 */
function scanDate(iso: string): string {
  return shortDateOrNull(iso) ?? '–';
}

/**
 * "Ayu Sepulloh" -> "AS". A free-text label, not a user record, so it can be a
 * single word, an email, or anything the operator typed — take the first letter
 * of up to two words and never assume a surname exists.
 */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function Businesses({
  onSelectBusiness,
}: {
  /** `view` is where in the business to land. Absent means its Overview. */
  onSelectBusiness: (id: string, view?: View) => void;
}) {
  // The view lives in the URL so "Documents (all clients)" deep-links, survives a reload,
  // and is still there on Back from a doc it opened. Switching replaces the
  // history entry: it is a view of the same page, not a place to go back to.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const view: BusinessesView = rawView === 'documents' ? 'documents' : rawView === 'today' && DELIVERY_ON ? 'today' : 'businesses';
  const setView = (next: BusinessesView) =>
    setSearchParams((params) => {
      if (next === 'documents' || next === 'today') {
        params.set('view', next);
        if (next === 'today') params.delete('show');
      } else {
        params.delete('view');
        // The documents filter means nothing on the table.
        params.delete('show');
      }
      return params;
    }, { replace: true });

  // The stage tab, in the URL like the view. Null means none was asked for, and
  // the page opens on Active plans (see `stage` below).
  const pickedStage = readStageTab(searchParams.get('stage'));
  // Find a client by typing (Jack, 22 Oct 2026). A search looks across every
  // stage, so a client is found without first guessing which tab holds it. In
  // the URL as ?q=, so the Cmd K jump and a shared link land on it too.
  // The box reads its own state, so fast typing never waits on the URL; the URL follows it.
  const [query, setQueryText] = useState(() => searchParams.get('q') ?? '');
  const setQuery = (next: string) => {
    setQueryText(next);
    setSearchParams((params) => {
      if (next) params.set('q', next);
      else params.delete('q');
      return params;
    }, { replace: true });
  };
  const queryWords = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);
  const searching = queryWords.length > 0;
  const searchRef = useRef<HTMLInputElement>(null);
  // "/" jumps to the box, as on most lists that have one.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const setStage = (next: StageTab) => {
    // A new tab opens on its own order: a sort picked for prospects' scores
    // means nothing on a tab of plan end dates.
    setSortPick(null);
    setQueryText('');
    setSearchParams((params) => {
      params.set('stage', next);
      // Picking a stage means "show me that stage": a search left in the box would hide it.
      params.delete('q');
      return params;
    }, { replace: true });
  };

  // #3507: All opens on the most recently scanned rather than the lowest score;
  // each tab has its own opening order (DEFAULT_SORT). Null is that default.
  // `lastScan` sorts by localeCompare on the raw ISO string — correct for
  // ISO-8601, and a never-scanned business compares as '' so it lands at the
  // bottom under desc, which is where it belongs.
  const [sortPick, setSortPick] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const navigate = useNavigate();
  // With the New scan switch on, "New scan" opens the full-page flow; off, today's modal.
  // Also the modal where the draft service is absent (switch on outside local dev without
  // the live switch): the flow's page would only say "not available here".
  const location = useLocation();
  const draftFlowOn = SCAN_DRAFT_UI_ON && scanDraftMode() !== 'absent';
  // The flow's Close comes back here (this view and its filters) rather than to a default.
  const flowOrigin = { from: `${location.pathname}${location.search}` };
  const openNewScan = () => {
    if (draftFlowOn) navigate(`${OURA_ROOT}/businesses/new`, { state: flowOrigin });
    else setScanOpen(true);
  };
  const continueDraft = (id: string) =>
    // No step named: the flow opens Review, or Start once the draft is approved.
    navigate(`${OURA_ROOT}/businesses/new?draft=${encodeURIComponent(id)}`, { state: flowOrigin });
  // "Draft saved." or "Scan started", handed over by the New scan page as it leaves.
  // The Scanning tag below is what stays; the snackbar only says it as you land.
  useDraftNoticeSnackbar();
  // New scan switch on: a scan running for a row's site, and new businesses whose scan
  // started but which are not on the list yet (sample server only).
  const scanning = useScanningSites();
  // null = still loading the first response; [] is a legitimate "no businesses
  // yet" once loaded. listBusinessesWithCounts() never throws (see @service/aeo) — a
  // failed fetch degrades to an empty list rather than blocking the screen,
  // same as the sibling listAssessments().
  const [businesses, setBusinesses] = useState<AeoBusiness[] | null>(null);
  // The server's tab counts for the same rows (ourteam-backend#6091); null when it sends none.
  const [serverCounts, setServerCounts] = useState<AeoStageCounts | null>(null);
  const jobs = useScanJobs();
  const snackbar = useSnackbar();

  // Every open draft, so a row can say "Continue the draft" (JOURNEY-SPEC v1.1 §2.1).
  // Null until the first read: the Drafts fold says nothing it does not know.
  const [rowDrafts, setRowDrafts] = useState<ScanDraftListItem[] | null>(null);
  /**
   * Whether the open drafts are known. Until they are, no row offers an action or the trash icon:
   * a row read as "no draft" would offer "Draft the 50" beside a draft already open (Jack, #4286).
   * A failed first read is said above the list, with Try again; it is never taken as "no drafts".
   * A later failed read keeps the list last read, which is a real list, not an empty one.
   */
  const [rowDraftsRead, setRowDraftsRead] = useState<'loading' | 'ready' | 'failed'>('loading');
  // New scan drafts saved on Close, each with Continue (switch on only): the same read as the rows.
  const openDrafts = useMemo(
    () => (rowDrafts ? rowDrafts.filter((d) => d.kind === 'new_scan' && draftPurpose(d) === d.kind) : null),
    [rowDrafts],
  );
  // The Client signed sheet for a scanned prospect, and the Rescan sheet for a failed scan.
  const [signedFor, setSignedFor] = useState<{ businessId: string; name: string; domain: string } | null>(null);
  const [rescanFor, setRescanFor] = useState<{ businessId: string; name: string; domain: string } | null>(null);

  // Clients being onboarded, by business id. Read beside the list rather than
  // joined into it server-side, so the list keeps working while the onboarding
  // endpoints are still being built. listOnboardings() never throws.
  const [onboardings, setOnboardings] = useState<Record<string, Onboarding>>({});
  /**
   * The plan facts (signature, published) for clients whose first check is
   * done, read as the Overview and Onboarding cards read them, so the row says
   * "Choose a question" or "Schedule not published" when they do. Taken from the
   * list record when it carries them (#6086); a record without them is read per
   * client (`getOnboardingPlanFacts`), as before.
   */
  const [plans, setPlans] = useState<Record<string, OnboardingPlanFacts>>({});
  /**
   * Whether each completed client's plan facts are read (New scan switch on only). Absent:
   * not asked yet, which is "loading", never "no plan" (Jack, #4284). A client keeps "ready"
   * through a later failed re-read: the facts last read still stand.
   */
  const [planState, setPlanState] = useState<Record<string, 'ready' | 'failed'>>({});
  /** Failed reads asked again, a few times, apart from the scan poll. */
  const planFailures = useRef(new Map<string, number>());
  const [planRetry, setPlanRetry] = useState(0);
  // When the earliest good read goes stale: the scheduler below has nothing else to wake it then.
  const [planExpiryAt, setPlanExpiryAt] = useState<number | null>(null);
  useEffect(() => {
    if (planExpiryAt === null) return;
    const timer = window.setTimeout(() => {
      setPlanExpiryAt(null);
      setPlanRetry((n) => n + 1);
    }, Math.max(0, planExpiryAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [planExpiryAt]);
  // Form answers the server could not file on any client. Read here, beside
  // the list, because seeing one must not require opening the right client —
  // not knowing which client it belongs to is the whole problem (#3977).
  const [unmatched, setUnmatched] = useState<UnmatchedAnswer[]>([]);
  /** The unmatched read failed: say so rather than showing nothing. */
  const [unmatchedFailed, setUnmatchedFailed] = useState(false);

  // The refresh that last STARTED wins. Two can be in flight — a poll and the
  // one that follows filing an answer — and the slower one used to land last
  // and restore the row that had just been filed, whose retry then 409s
  // (Jack, #3991).
  const refreshAt = useRef(0);

  // Per client: the plan read last asked for (only its answer lands), when a read last
  // succeeded (the short cache), and the reads on their way. A read dropped by an overlapping
  // refresh, or one that failed, has no good read and is asked again (Jack, #4297).
  const planReadSeq = useRef(new Map<string, number>());
  const planReadAt = useRef(new Map<string, number>());
  const completedNow = useRef(new Set<string>());
  const planInFlight = useRef(new Set<string>());
  // The clients the previous list response resolved from its own `planFacts` (#6086).
  const listResolvedPlans = useRef(new Set<string>());
  // Bumped when the plan cache is emptied, so the rows on screen are read again.

  /** Reads the rows. Plan facts are never read here: see the lazy read below, which reads each client once per PLAN_FACTS_TTL_MS. */
  const refetchBusinesses = useCallback(async () => {
    const mine = ++refreshAt.current;
    if (draftFlowOn) {
      void listOpenScanDrafts()
        .then((res) => {
          if (mine !== refreshAt.current) return;
          if (res.ok) {
            setRowDrafts(res.data);
            setRowDraftsRead('ready');
          } else {
            setRowDraftsRead((was) => (was === 'ready' ? was : 'failed'));
          }
        })
        .catch(() => {
          if (mine === refreshAt.current) setRowDraftsRead((was) => (was === 'ready' ? was : 'failed'));
        });
    }
    const [list, onboarding, stray] = await Promise.all([
      listBusinessesWithCounts(),
      showOnboarding ? listOnboardings() : Promise.resolve([]),
      showOnboarding ? listUnmatchedAnswers() : Promise.resolve({ rows: [], failed: false }),
    ]);
    if (mine !== refreshAt.current) return;
    setBusinesses(list.businesses);
    setServerCounts(list.stageCounts);
    setOnboardings(Object.fromEntries(onboarding.map((o) => [o.businessId, o])));
    const checked = onboarding.filter((o) => o.scan.status === 'complete');
    const current = new Set(checked.map((o) => o.businessId));
    // The plan facts the list carries (#6086) are taken as read, with no request per client. Only with
    // the New scan switch on, as the per-client read is. A record without them is read per client.
    const listed = draftFlowOn ? checked.filter((o) => o.planFacts !== undefined) : [];
    const listedIds = listed.map((o) => o.businessId);
    const listedSet = new Set(listedIds);
    const fromList: Record<string, OnboardingPlanFacts> = {};
    for (const o of listed) if (o.planFacts) fromList[o.businessId] = o.planFacts;
    completedNow.current = current;
    planReadAt.current = new Map(Array.from(planReadAt.current).filter(([id]) => current.has(id)));
    // A client the previous list resolved, and this list no longer carries facts for (the server's batch
    // failed for it): its TTL stamp goes, so the per-client read runs now instead of waiting out the TTL.
    for (const id of Array.from(listResolvedPlans.current)) if (!listedSet.has(id)) planReadAt.current.delete(id);
    listResolvedPlans.current = new Set(listedIds);
    for (const id of listedIds) {
      // A per-client read still in flight from before the list carried the facts must not land after them.
      planReadSeq.current.set(id, (planReadSeq.current.get(id) ?? 0) + 1);
      planInFlight.current.delete(id);
      planFailures.current.delete(id);
      planReadAt.current.set(id, Date.now());
    }
    setPlanState((was) => {
      const next: Record<string, 'ready' | 'failed'> = {};
      for (const [id, state] of Object.entries(was)) if (current.has(id)) next[id] = state;
      // `null` from the list is "no plan", a read that landed: ready, not loading.
      for (const id of listedIds) next[id] = 'ready';
      return next;
    });
    // A client that is no longer complete has no plan facts to show.
    setPlans((was) => {
      const next: Record<string, OnboardingPlanFacts> = {};
      for (const [id, plan] of Object.entries(was)) if (current.has(id) && !listedSet.has(id)) next[id] = plan;
      return { ...next, ...fromList };
    });
    setUnmatched(stray.rows);
    // "Nothing is waiting" and "I could not look" are different facts, and the
    // screen says which: a notice that vanishes on a failed read is the
    // silence this feature exists to end.
    setUnmatchedFailed(stray.failed);
  }, [draftFlowOn]);

  useEffect(() => {
    void refetchBusinesses();
  }, [refetchBusinesses]);

  // Whether any completed client's plan facts come from the list (#6086). Then the list is read again
  // on the TTL below, which is what the per-client read's own expiry did: a list left open does not
  // keep a signature or publish that has changed, and there is no per-client request to refresh them.
  const planFromList = useMemo(
    () => draftFlowOn && Object.values(onboardings).some((o) => o.scan.status === 'complete' && o.planFacts !== undefined),
    [draftFlowOn, onboardings],
  );
  useEffect(() => {
    if (!planFromList) return;
    const timer = window.setInterval(() => void refetchBusinesses(), PLAN_FACTS_TTL_MS);
    return () => window.clearInterval(timer);
  }, [planFromList, refetchBusinesses]);

  // A first check's draft builds by itself in about a minute: keep the row up with it, as Onboarding does.
  // A first check that runs ends by itself too: the row's scores and step follow it in place.
  const building = useMemo(() => Object.values(onboardings).some((o) => o.scan.draft?.status === 'building' || o.scan.status === 'running'), [onboardings]);
  useVisiblePoll(() => { if (building) void refetchBusinesses(); }, 3_000, [building, refetchBusinesses]);
  // A scan that stops reading as Scanning has finished: its row now carries the scored scan, so read the rows again.
  const startedKey = scanning.started.map((d) => d.draftId).join('|');
  const lastStarted = useRef(startedKey);
  useEffect(() => {
    const before = lastStarted.current.split('|').filter(Boolean);
    lastStarted.current = startedKey;
    if (before.some((id) => !startedKey.split('|').includes(id))) void refetchBusinesses();
  }, [startedKey, refetchBusinesses]);
  // A business opens in its own tab: coming back to this one reads the rows again, so an
  // onboarding step done there ("9 of 9") is not shown here as "4 of 9" (Angel, 15 Oct).
  // visibilitychange and focus both fire on the way back: one refresh, not two.
  useEffect(() => {
    let timer: number | undefined;
    const back = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      // The rows only: plan facts already read stay until their short cache runs out.
      timer = window.setTimeout(() => void refetchBusinesses(), 100);
    };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
    };
  }, [refetchBusinesses]);

  // A run reaching a terminal state means its business record just settled
  // server-side too (ourteam-backend#3747 upserts on assessment write) —
  // refetch so the finished scan lands as a real scored row without waiting
  // for a manual page reload. Live progress itself is reported by
  // OuraProcessingToast, not by a row in this table.
  // `cancelled` counts as settled: the BE writes a terminal row and keeps
  // whatever results the run produced before it stopped, so the business record
  // has changed and the table should show it — including releasing the domain,
  // which is the whole point of cancelling.
  const settledCount = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === 'complete' || j.status === 'partial' || j.status === 'failed'
          || j.status === 'cancelled',
      ).length,
    [jobs],
  );
  const prevSettledRef = useRef(settledCount);
  useEffect(() => {
    if (settledCount > prevSettledRef.current) void refetchBusinesses();
    prevSettledRef.current = settledCount;
  }, [settledCount, refetchBusinesses]);

  /**
   * THE PLAN QUEUE (#3714 AC6): signed, and no timeline published yet.
   *
   * Both halves come off the list row (ourteam-backend#4915) rather than a
   * fetch per business — the control carries a count, which means the whole set
   * and not the page on screen.
   *
   * `engagement_status` null is NOT in the queue: nobody has sold them anything,
   * so they are not waiting for a plan, they are waiting for a sale.
   */
  const isAwaitingPlan = (b: AeoBusiness) =>
    b.engagement_status === 'signed' && (b.timeline_published_version ?? null) === null;
  const [queueWanted, setQueueOnly] = useState(false);

  /**
   * A client still being set up. Its row goes to the Onboarding tab, which
   * replaces the Onboarding pill; the number that matters more is how many
   * are waiting on US, so the tab says that too.
   */
  /** A client's onboarding line, with its plan facts when read (as the Next cards have them). */
  /** With the switch on, a completed client's plan read: not asked or on its way is "loading". Off: absent, as on main. */
  const planReadOf = useCallback(
    (id: string): PlanRead | undefined => (draftFlowOn && onboardings[id]?.scan.status === 'complete' ? planState[id] ?? 'loading' : undefined),
    [draftFlowOn, onboardings, planState],
  );
  const summaryOf = useCallback(
    (id: string) => onboardingSummary(onboardings[id], Date.now(), { plan: plans[id] ?? null, planRead: planReadOf(id) }),
    [onboardings, plans, planReadOf],
  );
  const isOnboarding = useCallback(
    (b: AeoBusiness) => !!onboardings[b.id] && !summaryOf(b.id).complete,
    [onboardings, summaryOf],
  );
  const onboardingOursCount = useMemo(
    () => (businesses ?? []).filter((b) => isOnboarding(b) && summaryOf(b.id).who === 'staff').length,
    [businesses, isOnboarding, summaryOf],
  );
  // The Today tab leads with the onboarding Next cards that need us, read from the same records as the rows.
  const todayOnboarding = useMemo(
    () => ({ records: onboardings, plans, planReads: Object.fromEntries(Object.keys(onboardings).map((id) => [id, planReadOf(id)])) }),
    [onboardings, plans, planReadOf],
  );

  /** A row's stage: the list payload, its onboarding record and its open draft. */
  const stageOfRow = useCallback((b: AeoBusiness): HubStage => {
    const key = siteKey(b.domain ?? '');
    const draft = (rowDrafts ?? []).find((d) => d.businessId === b.id || (key && siteKey(d.domain) === key));
    const running = scanning.labelFor(b.domain, b.id) ? { percent: null } : null;
    return hubStage(factsFromRow(b, running), onboardings[b.id] ?? null, draft ? hubDraftOf(draft) : null, plans[b.id] ?? null, planReadOf(b.id));
  }, [rowDrafts, scanning, onboardings, plans, planReadOf]);

  /**
   * Carries out a row's press, as the business page does: the flow, a sheet, or
   * the business at the right screen. Every press stays in this tab (the flow's
   * Close comes back here); a press that belongs on Onboarding is made there on
   * arrival, so one press does it.
   */
  const actOnRow = useHubActions({
    onClientSigned: (r) => setSignedFor({ businessId: r.id, name: r.name, domain: r.domain }),
    onRescan: (r) => setRescanFor({ businessId: r.id, name: r.name, domain: r.domain }),
  });

  // Malaysian today, DATE-ONLY: what a plan's start and end dates compare against.
  const today = myToday();

  // Each client's Delivery, as the Tracker reads it: late work is a reason to act (Jack, 5 Nov 2026).
  // Off with Delivery: no reads, and late work is not a reason at all.
  const { load: boardLoad, reload: reloadBoard } = useBoard({ enabled: DELIVERY_ON });
  /** Whether that Delivery is known. Until it is, nothing is ranked as on track (absent means unknown). */
  const deliveryRead: DeliveryRead =
    !DELIVERY_ON || boardLoad.state === 'unavailable' ? 'off'
      : boardLoad.state === 'loading' ? 'loading'
        : boardLoad.state === 'error' ? 'error'
          : 'ready';
  const cardsById = useMemo(() => {
    const m = new Map<string, ClientCard>();
    if (DELIVERY_ON && boardLoad.state === 'ready') for (const c of boardLoad.clients) m.set(c.id, cardOf(c, today));
    return m;
  }, [boardLoad, today]);
  /** Clients whose own Delivery read failed: their late work is unknown, never none. */
  const deliveryFailed = useMemo(
    () => new Set(DELIVERY_ON && boardLoad.state === 'ready' ? boardLoad.failed.map((f) => f.id) : []),
    [boardLoad],
  );

  /**
   * A row's journey is known once the open drafts are read and, for a client whose first check
   * is done, its plan facts are read (or failed, which claims nothing). Before that the row has
   * no action and no trash icon, so nothing is clickable that could change a moment later.
   */
  const journeyReadyFor = useCallback(
    (b: AeoBusiness) => draftFlowOn && rowDraftsRead === 'ready' && planReadOf(b.id) !== 'loading',
    [draftFlowOn, rowDraftsRead, planReadOf],
  );

  /** Every business as a row, before any tab or pill narrows them. */
  const allRows = useMemo(() => (businesses ?? []).map((b) => {
    const la = b.latest_assessment;
    const quiet = quietSince(la?.captured_at ?? null, b.last_delivery_event_at ?? null);
    const record = onboardings[b.id];
    // Which tab the row is in: the server decides (ourteam-backend#6091), so a
    // running plan with set-up still open is Active, not Onboarding. An older
    // server sends no stage, and the one module that used to answer still does.
    const rowStage = b.stage ?? listStageOf(b, record ? (summaryOf(b.id).complete ? 'done' : 'open') : null, today);
    const isClientRow = rowStage === 'active' || rowStage === 'onboarding';
    const gated = !!la?.gated;
    const journeyReady = journeyReadyFor(b);
    const hub = journeyReady ? stageOfRow(b) : null;
    const action = hub ? rowAction(hub, b.business_name, { draftFlow: draftFlowOn, signOn: canSignClients }) : null;
    return {
      id: b.id,
      name: b.business_name,
      domain: b.domain,
      // The site's own icon, drawn in the lead square (how it sells shows while there is none).
      logo: faviconOf(b.favicon_url, b.domain),
      category: b.category,
      // Where customers find them, and how they buy (visit, online, both, home visits).
      where: whereOf(b),
      // seo_score: 0 is a real score — only null when there's no terminal
      // assessment at all, or the run was gated (no usable score).
      aeo: la && !gated ? la.aeo_score : null,
      seo: la && !gated ? la.seo_score : null,
      status: (!la ? 'pending' : gated ? 'gated' : 'scored') as RowStatus,
      stage: rowStage,
      // Present only while the client is being set up. Once every access is
      // held the row reads as any other client's.
      onboarding: isOnboarding(b) ? summaryOf(b.id) : null,
      // The plan, as the list row carries it. Each is null or absent until the
      // list sends plan dates; the cells say so rather than guess.
      planName: b.plan_name ?? null,
      termMonths: b.plan_term_months ?? null,
      planMonth: b.plan_month ?? null,
      startsOn: b.plan_started_on ?? null,
      endsOn: b.plan_ends_on ?? null,
      // "Plan not started" and "payment not cleared" are one fact on the server
      // (`lifecycle.state: signed`), and the only one the Set-up cell can name.
      notStarted: b.lifecycle?.state === 'signed',
      // Marked a client with no engagement on file: there is no plan to run.
      noPlan: (b.engagement_status ?? null) === null && !b.plan_name,
      awaitingPlan: isAwaitingPlan(b),
      timelineVersion: b.timeline_published_version ?? null,
      // Two different dates, deliberately shown side by side: `captured_at`
      // is when it was last assessed, `created_at` is when it entered the
      // pipeline (ourteam-backend#3793). A business added months ago and never
      // re-scanned is the one worth calling, and one column cannot say that.
      lastScan: la?.captured_at ?? null,
      // What ourteam-backend#4908 added: the last time we DELIVERED anything.
      // Null on a business nothing has ever been done for.
      lastDelivery: b.last_delivery_event_at ?? null,
      added: b.created_at ?? null,
      // Who commissioned the LAST scan (#3509) — not an owner of the business.
      // Null on every run captured before ourteam-backend#4568, which is why
      // the cell renders an em-dash rather than guessing a name.
      requestedBy: la?.requested_by ?? null,
      // Where the business stands, for its one row action and its trash icon (New scan switch on).
      hub,
      // Switch on and the journey not read yet: no action, no trash icon.
      hubPending: draftFlowOn && !journeyReady,
      // The row's one action, the Next card's own verb (§2.1).
      action,
      // A scan running for this site is the truth until it lands; the old status is not.
      running: scanning.labelFor(b.domain, b.id),
      // What to act on first, worst first (Jack, 5 Nov 2026). Clients only: a prospect's next step is a sale.
      focuses: isClientRow ? focusesOf({
        action,
        onboarding: isOnboarding(b) ? summaryOf(b.id) : null,
        settingUp: rowStage === 'onboarding',
        awaitingPlan: isAwaitingPlan(b),
        changePending: !!b.timeline_change_pending,
        aeo: la && !gated ? la.aeo_score : null,
        quietDays: quiet === null ? null : Math.max(0, Math.floor((Date.now() - quiet) / 86_400_000)),
        endsInDays: b.plan_ends_on ? daysUntil(b.plan_ends_on, today) : null,
        card: cardsById.get(b.id) ?? null,
        deliveryUnknown: deliveryRead === 'error' || deliveryFailed.has(b.id),
      }, today) : [],
    };
  }), [businesses, onboardings, isOnboarding, summaryOf, today, draftFlowOn, stageOfRow, scanning, journeyReadyFor, cardsById, deliveryRead, deliveryFailed]);

  /**
   * Rows per tab: the server's counts when it sends them (ourteam-backend#6091),
   * which are counted from the same rows as the stages. Otherwise counted here,
   * over every business, so the tabs add up to All.
   */
  const stageCounts = useMemo(() => {
    if (serverCounts) return serverCounts;
    const counts: Record<StageTab, number> = { active: 0, onboarding: 0, prospect: 0, ended: 0, all: allRows.length };
    for (const r of allRows) counts[r.stage] += 1;
    return counts;
  }, [allRows, serverCounts]);

  // Active plans unless another tab was asked for. A pipeline with no running
  // plan yet opens on All, not on an empty tab.
  const stage: StageTab = searching ? 'all' : pickedStage ?? (businesses !== null && stageCounts.active === 0 ? 'all' : 'active');
  const columns = COLUMNS[stage];
  // The picked sort, while this tab has that column; otherwise the tab's own order.
  const sortable = (key: SortKey) => key === 'name' || columns.some((c) => HEAD[c].sort === key);
  const { key: sort, dir } = sortPick && sortable(sortPick.key) ? sortPick : DEFAULT_SORT[stage];

  const tabRows = useMemo(
    () => {
      const inStage = stage === 'all' ? allRows : allRows.filter((r) => r.stage === stage);
      if (!searching) return inStage;
      return inStage.filter((r) => {
        const hay = `${r.name} ${r.domain} ${r.category ?? ''}`.toLowerCase();
        return queryWords.every((w) => hay.includes(w));
      });
    },
    [allRows, stage, searching, queryWords],
  );
  // The queue pill counts and filters what this tab shows.
  const awaitingPlanCount = useMemo(() => tabRows.filter((r) => r.awaitingPlan).length, [tabRows]);
  // An empty queue hides its pill, so it must not go on filtering.
  const queueOnly = queueWanted && awaitingPlanCount > 0;

  const rows = useMemo(() => {
    // The Start here order, as a number: the worst reason first, a late one ahead of its rank.
    const order = new Map(rankClients(tabRows).map((r, i) => [r.id, i]));
    const rankOf = (r: { id: string }) => order.get(r.id) ?? 0;
    const list = tabRows.filter((r) => !queueOnly || r.awaitingPlan);
    list.sort((a, b) => {
      // A plan with no end date on file sorts last in either direction: it is
      // not ending soonest, nor did it end most recently.
      if (sort === 'ends' && !a.endsOn !== !b.endsOn) return a.endsOn ? -1 : 1;
      const cmp =
        sort === 'name' ? a.name.localeCompare(b.name)
          // Never scanned: when it entered stands in, so a client signed today sits at the top, not the bottom.
          : sort === 'lastScan' ? (a.lastScan ?? a.added ?? '').localeCompare(b.lastScan ?? b.added ?? '')
            : sort === 'added' ? (a.added ?? '').localeCompare(b.added ?? '')
              : sort === 'ends' ? (a.endsOn ?? '').localeCompare(b.endsOn ?? '')
              // Quiet: longest since we last did anything, first. A business
              // nobody has ever scanned is the quietest there is, so a null
              // sorts ahead of every real date rather than to the bottom —
              // which is what `(a ?? '')` would do and why this is not just
              // `lastScan` reversed.
              : sort === 'quiet' ? quietRank(a.lastScan, a.lastDelivery) - quietRank(b.lastScan, b.lastDelivery)
                : sort === 'focus' ? rankOf(a) - rankOf(b)
                : sort === 'aeo' ? (a.aeo ?? -1) - (b.aeo ?? -1)
                  : (a.seo ?? -1) - (b.seo ?? -1);
      return dir === 'asc' ? cmp : -cmp;
    });
    // On the Onboarding tab, in its own order, the rows that need us come first, the late ones at the
    // very top, then the ones waiting on the client or on Oura (the old Onboarding filter's order).
    if (stage === 'onboarding' && !sortPick) {
      const band = (r: (typeof list)[number]) => (!r.onboarding ? 3 : r.onboarding.who !== 'staff' ? 2 : r.onboarding.late ? 0 : 1);
      list.sort((a, b) => band(a) - band(b));
    }
    return list;
  }, [tabRows, sort, dir, queueOnly, stage, sortPick]);

  // Start here: the clients on this tab with something to do, ranked; the rest are on track.
  const focusTab = stage === 'active' || stage === 'onboarding';
  const focusClients = useMemo(() => {
    const lead = (r: (typeof tabRows)[number]) => ({ id: r.id, name: r.name, logo: r.logo, leadTone: BUY_LEAD[r.where.buy].tone, leadIcon: BUY_LEAD[r.where.buy].icon, focuses: r.focuses });
    const ranked = rankClients(tabRows.map(lead));
    return { needs: ranked.filter((r) => r.focuses.length > 0), fine: ranked.filter((r) => r.focuses.length === 0) };
  }, [tabRows]);

  /**
   * Plan facts, read lazily (Jack, #4289, #4284). Only with the New scan switch on (an explicit
   * `=== "true"`, SCAN_DRAFT_UI_ON): switched off, this screen reads no plan facts at all, as on
   * main. Switched on, the clients on screen are read first (this tab's rows, or Today's
   * onboarding cards), then the other completed ones, since a row's tab depends on its plan;
   * PLAN_READ_PARALLEL at a time, at most PLAN_READ_CAP per pass, and a good read is kept for
   * PLAN_FACTS_TTL_MS, then read again by a timer set for the earliest expiry (so a list left open
   * does not keep a signature or publish that has changed). Focus, visibility and the build poll do
   * not read them again. Until a read lands a row says "Reading the plan", never "no plan".
   *
   * The list carries each client's plan facts (backend #6086, one batch per list read), so this
   * per-client read is only the fallback for a record without them: an older backend, or a failed
   * batch. Those bounds stay: nothing re-read inside the TTL, nothing re-read on focus, visibility or
   * the poll, never more than PLAN_READ_PARALLEL requests at once, and none at all with the switch off.
   */
  const planWanted = useMemo(() => {
    if (!draftFlowOn) return '';
    // The clients on screen first, then the other completed ones: a row's tab depends on its plan.
    const shown = view === 'today' ? Object.keys(onboardings) : rows.map((r) => r.id);
    const ids = Array.from(new Set([...shown, ...Object.keys(onboardings)]));
    // A record that carries its plan facts (#6086) is not read again here.
    return ids.filter((id) => onboardings[id]?.scan.status === 'complete' && onboardings[id]?.planFacts === undefined).join('|');
  }, [view, rows, onboardings, draftFlowOn]);
  useEffect(() => {
    if (!draftFlowOn || !planWanted) return;
    const now = Date.now();
    const allDue = planWanted
      .split('|')
      .filter((id) => !planInFlight.current.has(id) && now - (planReadAt.current.get(id) ?? -Infinity) >= PLAN_FACTS_TTL_MS)
      // A client whose reads failed PLAN_RETRY_MAX times stays "could not read the plan" until the
      // page is opened again, so the passes below always end.
      .filter((id) => (planFailures.current.get(id) ?? 0) <= PLAN_RETRY_MAX);
    const due = allDue.slice(0, PLAN_READ_CAP);
    const queue = [...due];
    // More clients than one pass reads: the next pass starts when this one ends, so no row waits
    // on "Reading the plan" for a read nobody asks for.
    let left = due.length;
    const more = allDue.length > due.length;
    const next = (): void => {
      const id = queue.shift();
      if (!id) return;
      const token = (planReadSeq.current.get(id) ?? 0) + 1;
      planReadSeq.current.set(id, token);
      planInFlight.current.add(id);
      void getOnboardingPlanFacts(id)
        .then(
          (plan) => {
            if (planReadSeq.current.get(id) !== token) return;
            if (!completedNow.current.has(id)) return;
            const readAt = Date.now();
            planReadAt.current.set(id, readAt);
            setPlanExpiryAt((current) => {
              const expiresAt = readAt + PLAN_FACTS_TTL_MS;
              return current === null ? expiresAt : Math.min(current, expiresAt);
            });
            planFailures.current.delete(id);
            setPlanState((was) => (was[id] === 'ready' ? was : { ...was, [id]: 'ready' }));
            setPlans((was) => {
              if (plan) return { ...was, [id]: plan };
              if (!(id in was)) return was;
              const rest = { ...was };
              delete rest[id];
              return rest;
            });
          },
          // A failed read keeps the plan already known. With none, the row says it could not read
          // the plan, and the read is asked again up to PLAN_RETRY_MAX times, PLAN_RETRY_MS apart.
          () => {
            if (planReadSeq.current.get(id) !== token) return;
            setPlanState((was) => (was[id] === 'ready' || was[id] === 'failed' ? was : { ...was, [id]: 'failed' }));
            const tries = (planFailures.current.get(id) ?? 0) + 1;
            planFailures.current.set(id, tries);
            if (tries <= PLAN_RETRY_MAX) window.setTimeout(() => setPlanRetry((n) => n + 1), PLAN_RETRY_MS);
          },
        )
        .finally(() => {
          if (planReadSeq.current.get(id) === token) planInFlight.current.delete(id);
          left -= 1;
          if (left === 0 && more) setPlanRetry((n) => n + 1);
          next();
        });
    };
    for (let i = 0; i < PLAN_READ_PARALLEL; i += 1) next();
  }, [planWanted, planRetry, draftFlowOn]);

  /**
   * A new business whose scan just started (New scan flow) before the list has
   * it: one provisional row at the top, linking back to the draft's Start page.
   * Only where prospects show, and not while a queue filter is on or once the
   * list has the site.
   */
  const provisional = useMemo(() => {
    if (businesses === null || queueOnly || (stage !== 'prospect' && stage !== 'all')) return [];
    const listed = new Set(businesses.map((b) => siteKey(b.domain ?? '')));
    return scanning.started.filter((d) => d.kind === 'new_scan' && !listed.has(siteKey(d.domain)));
  }, [businesses, queueOnly, stage, scanning.started]);

  /** Distinct categories already in use, for the New Scan category suggestions. */
  const categoryOptions = useMemo(
    () => Array.from(new Set((businesses ?? []).map((b) => b.category).filter(Boolean))).sort(),
    [businesses],
  );

  /**
   * Delete a business. Since ourteam-backend#5336 the BE deletes it for good,
   * with its scans, documents and uploaded files, and a re-scan of the same
   * domain starts a brand-new business. So the button, its tooltip and the
   * dialog all say "Delete": the tooltip went on saying "Remove from pipeline"
   * after the dialog had been reworded, which told a sighted hover and a screen
   * reader that the action could be undone. Staff-only server-side; a client
   * caller gets 403.
   */
  // The row awaiting confirmation. Holding the row (not just its id) lets the
  // modal name the business and its domain without re-searching the list, and
  // keeps that copy correct if the list refetches underneath it.
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; domain: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  /**
   * Whether removing the business awaiting confirmation would leave a scan
   * behind — drives the amber warning in the confirm modal.
   *
   * `untracked` counts here, unlike in `scanning` above. It means this browser
   * stopped watching, NOT that the run stopped, so the domain lock and the
   * "removing it will not stop the scan" hazard are both still live — and 30
   * minutes in, still-running is the more likely case, not the less.
   */
  const deleteTargetScanning = useMemo(
    () =>
      jobs.some(
        (j) =>
          j.domain === pendingDelete?.domain &&
          (j.status === 'queued' || j.status === 'running' || j.status === 'untracked'),
      ),
    [jobs, pendingDelete],
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { id, name } = pendingDelete;
    setDeleting(true);
    const ok = await deleteBusiness(id);
    setDeleting(false);
    // Close on both outcomes: leaving the dialog open on failure with a toast
    // behind it reads as though the click did not register.
    setPendingDelete(null);
    if (ok) {
      // "Removed from the pipeline" was true of a soft delete. It is a
      // deletion now (ourteam-backend#5338), and the toast is the last word a
      // staffer reads about it.
      snackbar.showSuccess(`${name} deleted`, 'Everything Oura held for it is gone.');
      void refetchBusinesses();
    } else {
      // NOT "nothing was removed". That is true of the backend's transactional
      // 500, which rolls back — but `deleteBusiness` answers false for a
      // timeout or a dropped response too, and those can land after the server
      // already committed an irreversible delete. Say what we know: the result
      // was not confirmed (Jack, #4001).
      snackbar.showError('Could not confirm whether the business was deleted', 'Refresh the list before trying again.');
    }
  };

  const toggle = (key: SortKey) =>
    setSortPick(sort === key ? { key, dir: dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const ariaSort = (key: SortKey) => (sort === key ? (dir === 'asc' ? 'ascending' : 'descending') : undefined);
  /**
   * A sortable head, as Finder's list view does it: only the column in use
   * shows its arrow, the rest darken on hover. An empty arrow slot on every
   * head cost the table its fit on a laptop.
   */
  const sortHead = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => toggle(key)}
      className={cn('inline-flex items-center gap-1 rounded hover:text-gray-900', sort === key && 'text-gray-900', FOCUS_RING)}
    >
      {label}
      {sort === key && (dir === 'asc' ? <ChevronUpIcon aria-hidden size={12} /> : <ChevronDownIcon aria-hidden size={12} />)}
    </button>
  );

  // A client being set up opens on its onboarding: that is the screen with
  // the next thing to do, and one that has never been scanned has no Overview.
  const open = (id: string) => onSelectBusiness(id, onboardings[id] && !summaryOf(id).complete ? 'onboarding' : undefined);

  type Row = (typeof rows)[number];

  /**
   * "Month 4 of 12" while the term runs; the term alone before it starts,
   * where a month number would be the server's placeholder 1, and after it
   * ends, where "Month 12 of 12" would read as still running.
   */
  const termText = (r: Row): string | null => {
    if (!r.termMonths) return null;
    const running = !!r.startsOn && r.startsOn <= today && !(r.endsOn && r.endsOn < today);
    return running && r.planMonth ? `Month ${r.planMonth} of ${r.termMonths}` : `${r.termMonths} months`;
  };

  const none = <span className="text-gray-400">{EMPTY_CELL}</span>;
  /** A dot and a word on one line, the house status mark. */
  const dotted = (dot: string, text: ReactNode, textClass = 'text-gray-700') => (
    <span className={cn('inline-flex max-w-full items-center gap-2', textClass)}>
      <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', dot)} />
      <span className="truncate">{text}</span>
    </span>
  );

  const cell = (c: ColKey, r: Row) => {
    const td = (content: ReactNode, extra?: string, title?: string) => (
      <td key={c} title={title} className={cn(TD, HEAD[c].align === 'center' && 'text-center', tierOf(stage, c), extra)}>
        {content}
      </td>
    );
    switch (c) {
      case 'next':
        return td(
          <NextUp focuses={r.focuses} checking={deliveryRead === 'loading'} onPress={(f) => (f.rowAction && r.action ? actOnRow(r, r.action.act) : onSelectBusiness(r.id, f.view))} />,
          'max-w-[15rem]',
        );
      case 'plan':
        return td(r.planName ?? none, 'text-gray-900');
      case 'term': {
        const running = !!r.termMonths && !!r.planMonth && !!r.startsOn && r.startsOn <= today && !(r.endsOn && r.endsOn < today);
        if (running) {
          const soon = !!r.endsOn && daysUntil(r.endsOn, today) <= RENEWAL_DAYS;
          return td(<TermBar month={r.planMonth as number} of={r.termMonths as number} endsSoon={soon} />);
        }
        return td(termText(r) ?? none, 'tabular-nums text-gray-700');
      }
      case 'ends':
      case 'endedOn': {
        if (!r.endsOn) return td(none);
        const days = daysUntil(r.endsOn, today);
        // Only a running plan has a renewal to raise; an ended one is just a date.
        const soon = c === 'ends' && days <= RENEWAL_WINDOW_DAYS;
        return soon
          ? td(dotted('bg-amber-500', scanDate(r.endsOn), 'font-medium text-amber-700'), 'tabular-nums', untilLabel(days))
          : td(scanDate(r.endsOn), 'tabular-nums text-gray-700');
      }
      case 'timeline':
        // The plan queue, said on the row as well as counted on the pill.
        if (r.awaitingPlan) return td(dotted('bg-amber-500', 'Not published', 'text-amber-700'));
        return td(r.timelineVersion !== null ? 'Published' : none, 'text-gray-700');
      case 'setUp':
        return td(r.onboarding ? `${r.onboarding.done} of ${r.onboarding.total}` : none, 'tabular-nums text-gray-900');
      case 'nextStep':
        // Why the client is not running yet, first answer that applies.
        if (r.onboarding) return td(r.onboarding.line, r.onboarding.late ? LATE_TEXT : 'text-gray-700');
        if (r.noPlan) return td('No plan on file', 'font-medium text-amber-700');
        if (r.startsOn && r.startsOn > today) return td(`Starts ${scanDate(r.startsOn)}`, 'text-gray-700');
        if (r.notStarted) return td('Waiting for payment', 'text-gray-700');
        return td(none);
      case 'aeo':
      case 'seo': {
        // A gated run says so where its score would be: that is the question a
        // reader has when the number is missing.
        if (r.running) return c === 'aeo' ? td(<Tag tone="blue">{r.running}</Tag>) : td(none);
        if (r.status === 'gated') {
          return c === 'aeo' ? td('Gated', 'text-rose-600', 'The last scan ran and produced no usable score.') : td(none);
        }
        const v = c === 'aeo' ? r.aeo : r.seo;
        return td(v === null ? none : <ScoreBar v={v} />);
      }
      case 'stage':
        return td(dotted(LIST_STAGE_DOT[r.stage], LIST_STAGE_LABEL[r.stage]));
      case 'category':
        return td(r.category || none, 'text-gray-700');
      case 'where':
        return td(r.where.place ?? none, 'text-gray-700');
      case 'sells':
        return td(BUY_LABEL[r.where.buy], 'text-gray-700');
      case 'lastScan':
        return td(r.lastScan ? scanDate(r.lastScan) : 'Never', cn('tabular-nums', r.lastScan ? 'text-gray-500' : 'text-gray-400'));
      case 'quiet': {
        // How long it has been, not when it was — the Last scan cell already
        // says when. "Never" is the loud one: it is a client on the books that
        // nothing has ever measured.
        const since = quietSince(r.lastScan, r.lastDelivery);
        return since === null
          ? td('Never', 'text-rose-600')
          : td(quietLabel(since), cn('tabular-nums', quietTone(since)));
      }
      case 'requestedBy':
        return td(r.requestedBy ? (
          <span className="inline-flex max-w-full items-center gap-2 text-gray-700">
            <span aria-hidden className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
              {initials(r.requestedBy)}
            </span>
            <span className="truncate">{r.requestedBy}</span>
          </span>
        ) : none);
      case 'added':
        return td(r.added ? scanDate(r.added) : none, 'tabular-nums text-gray-500');
      case 'website':
        return td(r.domain, 'text-gray-500');
    }
  };

  // Portfolio roll-up (replaces the retired Overview tab's headline numbers).
  const stats = useMemo(() => {
    const scored = rows.filter((r) => r.aeo !== null && r.seo !== null);
    const avg = (pick: (r: (typeof rows)[number]) => number | null) =>
      scored.length
        ? Math.round(scored.reduce((n, r) => n + (pick(r) ?? 0), 0) / scored.length)
        : null;
    return {
      total: rows.length,
      scored: scored.length,
      avgAeo: avg((r) => r.aeo),
      avgSiteHealth: avg((r) => r.seo),
      needAttention: scored.filter((r) => (r.aeo ?? 0) < 50 || (r.seo ?? 0) < 50).length,
      endingSoon: rows.filter((r) => r.endsOn && daysUntil(r.endsOn, today) <= RENEWAL_WINDOW_DAYS).length,
    };
  }, [rows, today]);

  const onStarted = (result: StartResult) => {
    if (result.ok) {
      snackbar.showSuccess(
        `Scan started for ${result.job.domain}`,
        `About ${FIRST_CHECK_MINUTES} minutes. Progress shows in the panel at the bottom right.`,
      );
      return;
    }
    switch (result.kind) {
      // 409 — a run is already in flight for this domain (per #3065: link, don't double-submit).
      case 'duplicate': {
        const { existing } = result;
        const progress = existing.stages_total > 0
          ? ` (${existing.stages_complete}/${existing.stages_total})`
          : '';
        snackbar.showError(
          `${existing.domain} is already being scanned`,
          `Currently ${existing.stage.toLowerCase()}${progress} — no duplicate run started.`,
        );
        break;
      }
      case 'validation':
        snackbar.showError('Could not start the scan', result.errors.join(' · '));
        break;
      default:
        snackbar.showError('Could not start the scan', result.message);
    }
  };

  return (
    // Capped and centred: on a wide monitor an uncapped table spread its
    // columns a hand's width apart.
    <div className="mx-auto max-w-[1280px]">
      {/* Ask Oura (staff only, behind VITE_ENABLE_OURA_ASK): the All clients chat, above the title. */}
      {ASK_OURA_ON && <AskOuraBar className="mb-6" />}
      <PageHead
        title="Businesses"
        sentence={
          view === 'documents'
            ? 'What waits on clients and on us, across every client.'
            : 'Every business Oura has scanned, by where its plan stands.'
        }
        actions={view === 'businesses' ? (
            // Never disabled while a scan runs. Scans are concurrent — the
            // progress toast reports "2 scans running" quite happily — so
            // blocking the CTA stopped a second, unrelated domain from being
            // started for the twenty-five minutes the first one takes.
            //
            // The duplicate it was guarding against is guarded properly and in
            // the right place: POST /assessments returns 409 with the existing
            // in-flight id for the SAME domain (aeo.py:148-160). A global button
            // lock cannot express "this domain only", and reaching for one meant
            // the button was wrong about every other domain.
            //
            // Progress lives in the toast, which is a better home for it than a
            // CTA that then cannot be pressed.
            <>
              {canCreateClient && (
                <PillButton icon={UserPlusIcon} data-new-client-open="" onClick={() => setClientOpen(true)}>
                  New client
                </PillButton>
              )}
              {/* Every answer to the onboarding form and whether it arrived (#4065):
                  one click from the list staff start on. */}
              {FORM_ANSWERS_ON && (
                <PillLink to={ouraHref('formAnswers', { role: 'oura-staff' })} icon={InboxIcon}>
                  Form answers
                </PillLink>
              )}
              <PillButton variant="primary" icon={ScanLineIcon} onClick={openNewScan}>
                New scan
              </PillButton>
            </>
        ) : undefined}
      />

      {/* Above the views, not inside one: an answer nobody can place is a thing
          to deal with before reading the list, and it would be missed in a view
          a staffer may never open. Renders nothing when none are waiting. */}
      {showOnboarding && (
        <>
          {/* A failed read is said, not hidden: the notice disappearing is
              indistinguishable from "nothing is waiting", which is the silence
              this card exists to end (Jack, #3991). */}
          {unmatchedFailed && (
            <div role="alert" className={cn(SURFACE.card, 'mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3')}>
              <span className="text-sm text-gray-700">Could not check for form answers with no client.</span>
              <PillButton variant="text" onClick={() => void refetchBusinesses()}>Try again</PillButton>
            </div>
          )}
          <UnmatchedAnswersNotice
            rows={unmatched}
            onboardings={onboardings}
            onFiled={() => void refetchBusinesses()}
            onDismissed={() => void refetchBusinesses()}
          />
        </>
      )}

      {/* Businesses, Today (every client's next steps, from Delivery) or All
          clients (every client's docs), kept in the URL as ?view= so the view
          deep-links and survives a reload. The Client tab has its own
          Documents card. */}
      <Seg<BusinessesView>
        as="tablist"
        ariaLabel="Businesses view"
        value={view}
        onChange={setView}
        panelId={VIEW_PANEL_ID}
        className="mb-5"
        items={[
          { key: 'businesses', label: 'Businesses' },
          // Every client's next steps, from Delivery (proposal v1.6, 30 Sep 2026).
          ...(DELIVERY_ON ? [{ key: 'today' as const, label: 'Today' }] : []),
          { key: 'documents', label: 'Documents (all clients)' },
        ]}
      />

      {/* One panel that stays mounted, so every tab's aria-controls points at it. */}
      <div role="tabpanel" id={VIEW_PANEL_ID} aria-labelledby={`${VIEW_PANEL_ID}-tab-${view}`}>
      {view === 'today' ? (
        <TodayList onboarding={todayOnboarding} />
      ) : view === 'documents' ? (
        <AllClientDocs />
      ) : (
        <>
          {draftFlowOn && rowDraftsRead === 'failed' && (
            <div role="alert" className={cn(SURFACE.card, 'mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3')}>
              <span className="text-sm text-gray-700">Could not read the open drafts, so row actions wait until they are read.</span>
              <PillButton variant="text" onClick={() => void refetchBusinesses()}>Try again</PillButton>
            </div>
          )}

          {/* New scan drafts saved on Close: no business exists for them yet,
              so they are listed here, above the businesses, with Continue. */}
          {openDrafts && openDrafts.length > 0 && (
            <FoldScope value="staff-businesses">
              <Surface className="mb-5">
                <GroupFold id="drafts" tone="gray" title="Drafts" count={openDrafts.length} lead="icon">
                  <OpenDraftRows drafts={openDrafts} onContinue={continueDraft} />
                </GroupFold>
              </Surface>
            </FoldScope>
          )}

          {/* The stage filter above everything it filters: the tiles and the
              table both count the stage picked here. */}
          <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2">
            <SearchBox
              ref={searchRef}
              value={query}
              onChange={setQuery}
              placeholder="Find a client"
              label="Find a client by name or website"
              className="w-full sm:w-56"
            />
            <StagePills value={stage} counts={businesses === null ? null : stageCounts} onChange={setStage} />
            {/* The Onboarding pill used to carry this: of the clients being set
                up, how many wait on us. It sits beside the stage that holds them. */}
            {stage === 'onboarding' && onboardingOursCount > 0 && (
              <span className={cn(TYPE.tc, 'font-medium text-amber-700')}>{onboardingOursCount} waiting on us</span>
            )}
            {/* One click from the list header (AC6). Hidden entirely when the
                stage has no one in the queue: a control that always reads "0"
                trains people to stop looking at it, and an empty queue is the
                state we want. */}
            {awaitingPlanCount > 0 && (
              <>
              {/* A hairline apart from the stages: it narrows the stage on screen, it is not a stage. */}
              <span aria-hidden className="hidden h-5 w-px bg-gray-200 sm:block" />
              <button
                type="button"
                aria-pressed={queueOnly}
                onClick={() => setQueueOnly((v) => !v)}
                title="Signed clients whose schedule has never been published. They have no dates yet."
                aria-label={`Schedule not published, ${awaitingPlanCount}`}
                className={pillClass(queueOnly)}
              >
                Schedule not published
                <span className={cn('tabular-nums', queueOnly ? 'text-blue-500' : 'text-amber-600')}>{awaitingPlanCount}</span>
              </button>
              </>
            )}
          </div>

          <NewClientDialog
            open={clientOpen}
            onClose={() => setClientOpen(false)}
            onCreated={() => void refetchBusinesses()}
            onOpen={(id) => onSelectBusiness(id, 'onboarding')}
            returnFocus={() => document.querySelector<HTMLElement>('[data-new-client-open]')}
          />

          {draftFlowOn && (
            <>
              <NewClientDialog
                open={signedFor !== null}
                onClose={() => setSignedFor(null)}
                existing={signedFor ?? undefined}
                onCreated={() => void refetchBusinesses()}
                onOpen={(id) => { setSignedFor(null); onSelectBusiness(id, 'onboarding'); }}
              />
              <RescanSheet
                open={rescanFor !== null}
                businessId={rescanFor?.businessId ?? ''}
                businessName={rescanFor?.name ?? ''}
                domain={rescanFor?.domain ?? ''}
                setVersion={null}
                costUsd={null}
                nextScanAt={null}
                onClose={() => setRescanFor(null)}
                onStarted={() => {
                  setRescanFor(null);
                  snackbar.showSuccess('Rescan started', `About ${FIRST_CHECK_MINUTES} minutes.`);
                }}
              />
            </>
          )}

          <NewScanModal
            open={scanOpen}
            onClose={() => setScanOpen(false)}
            onStarted={onStarted}
            categoryOptions={categoryOptions}
          />

          {/* Clients: what to act on first, and how their scores and Delivery sit (Jack, 5 Nov 2026).
              Prospects and ended plans keep the three answer tiles. */}
          {focusTab && !searching ? (
            <Bento layout="c21xl" className="mb-6">
              <StartHere
                clients={focusClients.needs}
                onTrack={focusClients.fine}
                delivery={deliveryRead}
                onRetry={reloadBoard}
                onOpen={(id, v) => onSelectBusiness(id, v)}
                onAct={(id) => {
                  const r = rows.find((x) => x.id === id) ?? allRows.find((x) => x.id === id);
                  if (r?.action) actOnRow(r, r.action.act);
                  else open(id);
                }}
              />
              <BentoStack>
                <ScoreSpread rows={tabRows.map((r) => ({ ...r, icon: BUY_LEAD[r.where.buy].icon }))} onOpen={(id) => open(id)} />
                {deliveryRead !== 'off' && (
                  <DeliveryHealth
                    status={deliveryRead}
                    onRetry={reloadBoard}
                    cards={tabRows.map((r) => cardsById.get(r.id)).filter((c): c is ClientCard => !!c)}
                    unread={tabRows.filter((r) => deliveryFailed.has(r.id)).map((r) => r.name)}
                  />
                )}
              </BentoStack>
            </Bento>
          ) : (
          <Tiles cols={3} className="mb-6">
            <Tile
              label="Avg AEO score"
              icon={{ icon: EyeIcon, area: 'ai' }}
              loading={businesses === null}
              value={stats.avgAeo === null ? EMPTY_CELL : <OfValue n={stats.avgAeo} of={100} />}
              sub={stats.scored > 0 ? `Across ${stats.scored} scored` : 'Nothing scored here yet'}
            />
            <Tile
              label="Avg site health"
              icon={{ icon: ActivityIcon, area: 'site' }}
              loading={businesses === null}
              value={stats.avgSiteHealth === null ? EMPTY_CELL : <OfValue n={stats.avgSiteHealth} of={100} />}
              sub="Crawling, on-page signals, broken links"
            />
            {/* On the clients we work for, the call to make is the renewal. */}
            {stage === 'active' ? (
              <Tile
                label={`Ending in ${RENEWAL_WINDOW_DAYS} days`}
                icon={{ icon: CalendarClockIcon, area: 'dates' }}
                loading={businesses === null}
                value={<OfValue n={stats.endingSoon} unit={stats.endingSoon === 1 ? 'client' : 'clients'} />}
                sub={stats.endingSoon > 0 ? 'Raise the renewal now' : 'No renewals due'}
              />
            ) : (
              <Tile
                label="Need attention"
                icon={{ icon: AlertTriangleIcon, area: 'asks' }}
                loading={businesses === null}
                value={stats.needAttention}
                sub="AEO or site health under 50"
              />
            )}
          </Tiles>
          )}

          <div className={cn(SURFACE.card, 'overflow-hidden')}>
            <div className="relative overflow-x-auto">
              {/* Every column sized to what it holds (`w-px` on each head), the
                  little space left shared out in proportion, and the page capped
                  at 1280px so a wide monitor cannot pull the columns apart. On a
                  phone the table scrolls sideways rather than squeezing. */}
              <table className="w-full min-w-max border-collapse">
                <thead>
                  <tr>
                    <th className={cn(TH, 'text-left')} aria-sort={ariaSort('name')}>
                      {sortHead('Business', 'name')}
                    </th>
                    {columns.map((c) => {
                      const head = HEAD[c];
                      return (
                        <th
                          key={c}
                          title={head.title}
                          aria-sort={head.sort ? ariaSort(head.sort) : undefined}
                          className={cn(TH, head.align === 'center' ? 'text-center' : 'text-left', tierOf(stage, c))}
                        >
                          {head.sort ? sortHead(head.label, head.sort) : head.label}
                        </th>
                      );
                    })}
                    <th className="h-11 w-14"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {businesses === null ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="border-t border-gray-100 px-5 py-16 text-center">
                        <Loader2Icon aria-label="Loading" className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                      </td>
                    </tr>
                  ) : rows.length === 0 && provisional.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="border-t border-gray-100 px-5 py-16 text-center">
                        <p className="m-0 text-sm font-medium text-gray-900">{searching ? `No client matches "${query.trim()}"` : EMPTY_TAB[stage].title}</p>
                        <p className={cn(TYPE.tc, 'm-0 mt-1 text-gray-500')}>{searching ? 'Searched every stage, by name and website.' : EMPTY_TAB[stage].description}</p>
                        {!searching && EMPTY_TAB[stage].scan && (
                          <PillButton variant="primary" icon={ScanLineIcon} className="mt-4" onClick={openNewScan}>
                            New scan
                          </PillButton>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  {provisional.map((d) => (
                    <tr key={d.draftId}>
                      <td className={TD}>
                        <div className="flex max-w-[13rem] items-center gap-3 min-[1680px]:max-w-[17rem]">
                          <LogoSquare src={faviconOf(null, d.domain)} tone="gray" icon={Building2Icon} />
                          <div className="min-w-0">
                            {d.businessId ? (
                              // Oura has the business: its page shows the running scan, never the draft's dead end.
                              <button
                                type="button"
                                onClick={() => open(d.businessId as string)}
                                className={cn('block max-w-full truncate rounded text-left font-medium text-gray-900 hover:text-blue-600', FOCUS_RING)}
                              >
                                {d.businessName}
                              </button>
                            ) : (
                              <Link
                                to={`${OURA_ROOT}/businesses/new?draft=${encodeURIComponent(d.draftId)}&step=start`}
                                // The flow's Close comes back to this view and its filters, as from New scan.
                                state={flowOrigin}
                                className={cn('block truncate rounded font-medium text-gray-900 hover:text-blue-600', FOCUS_RING)}
                              >
                                {d.businessName}
                              </Link>
                            )}
                            <div className={cn(TYPE.tc, 'truncate text-gray-500')}>{d.domain}</div>
                          </div>
                        </div>
                      </td>
                      <td colSpan={columns.length + 1} className={TD}>
                        <Tag tone="blue">{scanning.labelFor(d.domain) ?? 'Scanning'}</Tag>
                      </td>
                    </tr>
                  ))}
                  {rows.map((r) => {
                    const lead = BUY_LEAD[r.where.buy];
                    return (
                      <tr key={r.id} className="group cursor-pointer transition-colors hover:bg-gray-50" onClick={() => open(r.id)}>
                        <td className={TD} title={`${r.name} · ${r.domain}`}>
                          {/* Capped so one long name cannot widen the table; the title shows it whole. */}
                          <div className="flex max-w-[13rem] items-center gap-3 min-[1680px]:max-w-[17rem]">
                            <LogoSquare src={r.logo} tone={lead.tone} icon={lead.icon} />
                            {/* The name is the keyboard's way in: the row is a click target
                                only. It opens where the row does (Onboarding while set-up is open). */}
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  open(r.id);
                                }}
                                className={cn('block max-w-full truncate rounded text-left font-medium text-gray-900 hover:text-blue-600', FOCUS_RING)}
                              >
                                {r.name}
                                {r.where.buy !== 'visit' && <span className="sr-only">, {BUY_LABEL[r.where.buy]}</span>}
                              </button>
                              {/* The row's one action, the Next card's own verb (§2.1). Always shown, under the
                                  name, so it is there on a phone and at any width without scrolling the table. */}
                              {/* Under Next up, the action is that column's first line: said once. */}
                              {r.action && !columns.includes('next') ? (
                                <button
                                  type="button"
                                  // The verb alone repeats down the list: the name says which row.
                                  aria-label={`${r.action.label}, ${r.name}`}
                                  onClick={(e) => { e.stopPropagation(); if (r.action) actOnRow(r, r.action.act); }}
                                  className={cn(
                                    TYPE.tc,
                                    '-ml-1 block whitespace-nowrap rounded px-1 font-medium text-blue-600 hover:text-blue-700 hover:underline max-sm:min-h-9',
                                    FOCUS_RING,
                                  )}
                                >
                                  {r.action.label}
                                </button>
                              ) : r.running && !columns.includes('aeo') ? (
                                <Tag tone="blue">{r.running}</Tag>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        {columns.map((c) => cell(c, r))}
                        <td className="h-[52px] border-t border-gray-100 pr-4 text-right align-middle">
                          {/* stopPropagation: the whole row is a click target that
                              opens the business — removing it must not also navigate.
                              Shown on hover where there is one; always on touch. */}
                          {!r.hubPending && (!r.hub || canTrash(r.hub)) && <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDelete({ id: r.id, name: r.name, domain: r.domain });
                            }}
                            title="Delete this business"
                            aria-label={`Delete ${r.name}`}
                            className={cn(
                              'rounded-full p-1.5 text-gray-400 transition hover:bg-rose-50 hover:text-rose-600 focus-visible:opacity-100',
                              '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100',
                              FOCUS_RING,
                            )}
                          >
                            <Trash2Icon aria-hidden size={15} />
                          </button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </div>

      <DeleteBusinessModal
        open={pendingDelete !== null}
        businessId={pendingDelete?.id ?? ''}
        businessName={pendingDelete?.name ?? ''}
        domain={pendingDelete?.domain ?? ''}
        deleting={deleting}
        scanRunning={deleteTargetScanning}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
